import React, { createContext, useContext, useState, useEffect } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';

interface ThemeContextType {
  isDarkTheme: boolean;
  toggleTheme: () => void;
  setTheme: (isDark: boolean) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

/**
 * Repeint le fond du DOCUMENT (html/body/#root), peint AVANT React et donc hors de portée de
 * tout composant.
 *
 * ⚠️ SANS LUI, LE THÈME CLAIR RESTAIT SUR FOND NUIT au rebond du défilement et partout où
 * l'application ne peint pas elle-même. Le script d'amorçage de `web/index.html` pose la même
 * variable au chargement ; celui-ci la met à jour à la BASCULE, sans recharger la page.
 * Sans effet en natif : `document` n'y existe pas.
 */
const peindreLeDocument = (dark: boolean) => {
  try {
    (globalThis as any).document?.documentElement?.style?.setProperty(
      '--cc-shell-bg', dark ? '#0a0e1a' : '#e8ebf0');
  } catch { /* natif, ou DOM indisponible : rien à peindre */ }
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDarkTheme, setIsDarkTheme] = useState(true);

  useEffect(() => {
    loadThemePreference();
  }, []);

  useEffect(() => { peindreLeDocument(isDarkTheme); }, [isDarkTheme]);

  const loadThemePreference = async () => {
    try {
      const savedTheme = await AsyncStorage.getItem('theme');
      if (savedTheme === 'dark') {
        setIsDarkTheme(true);
      } else if (savedTheme === 'light') {
        setIsDarkTheme(false);
      }
      // Pas de preference enregistree : conserver le defaut dark
    } catch (error) {
      console.error('Error loading theme preference:', error);
    }
  };

  const saveThemePreference = async (isDark: boolean) => {
    try {
      await AsyncStorage.setItem('theme', isDark ? 'dark' : 'light');
    } catch (error) {
      console.error('Error saving theme preference:', error);
    }
  };

  const toggleTheme = () => {
    const newTheme = !isDarkTheme;
    setIsDarkTheme(newTheme);
    saveThemePreference(newTheme);
  };

  const setTheme = (isDark: boolean) => {
    setIsDarkTheme(isDark);
    saveThemePreference(isDark);
  };

  return (
    <ThemeContext.Provider value={{ isDarkTheme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}; 
